import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '../queries/queryKeys';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type TransactionInsert = TablesInsert<'transactions'>;
type TransactionUpdate = TablesUpdate<'transactions'>;

/**
 * Add a new transaction
 * Automatically invalidates transaction and budget queries
 */
export function useAddTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transaction: Omit<TransactionInsert, 'user_id'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('transactions')
        .insert({ ...transaction, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all transaction queries
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      // Also invalidate budgets since spending depends on transactions
      queryClient.invalidateQueries({ queryKey: queryKeys.budgets.all });
    },
  });
}

/**
 * Update an existing transaction
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: TransactionUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.budgets.all });
    },
  });
}

/**
 * Delete a transaction
 */
export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.budgets.all });
    },
  });
}

/**
 * Transfer between accounts (creates two transactions)
 */
export function useTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transfer: {
      amount: number;
      fromAccount: string;
      fromAccountId: string;
      toAccount: string;
      toAccountId: string;
      description?: string;
      date: string;
      is_backdated?: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create expense from source account
      const { error: expenseError } = await supabase.from('transactions').insert({
        user_id: user.id,
        type: 'expense',
        amount: transfer.amount,
        category: 'Transfer',
        account: transfer.fromAccount,
        account_id: transfer.fromAccountId,
        description: `Transfer to ${transfer.toAccount}${transfer.description ? `: ${transfer.description}` : ''}`,
        date: transfer.date,
        is_backdated: transfer.is_backdated || false,
      });
      if (expenseError) throw expenseError;

      // Create income to destination account
      const { error: incomeError } = await supabase.from('transactions').insert({
        user_id: user.id,
        type: 'income',
        amount: transfer.amount,
        category: 'Transfer',
        account: transfer.toAccount,
        account_id: transfer.toAccountId,
        description: `Transfer from ${transfer.fromAccount}${transfer.description ? `: ${transfer.description}` : ''}`,
        date: transfer.date,
        is_backdated: transfer.is_backdated || false,
      });
      if (incomeError) throw incomeError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
    },
  });
}
